import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { SENSORS_URL } from '../Constants';
import './TimeRange.css';
import {
  TIME_RANGE_PARTS,
  getCurrentTimeRangePartValues,
  isTimeRangePartEnabled,
} from './timeRangeParts';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toFiniteNumber = (value, fallback = 0) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeIntegerString = (value, fallback = 0) =>
  String(Math.round(toFiniteNumber(value, fallback)));

const getPartPadLength = (partKey, showLeadingZeroHours, showLeadingZeroMinutes) => {
  if (partKey === 'year') return 4;
  if (partKey === 'hours') return showLeadingZeroHours ?? true ? 2 : 0;
  if (partKey === 'minutes') return showLeadingZeroMinutes ?? true ? 2 : 0;
  return 2;
};

const getDisplayFallback = (padLength) => '0'.repeat(Math.max(1, padLength || 1));

const formatPartValue = (value, padLength = 0) => {
  if (value === null || value === undefined || value === '') {
    return getDisplayFallback(padLength);
  }

  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) {
    const integerValue = Math.trunc(numericValue);
    const sign = integerValue < 0 ? '-' : '';
    const digits = String(Math.abs(integerValue));
    return `${sign}${padLength > 0 ? digits.padStart(padLength, '0') : digits}`;
  }

  const safeValue = String(value).trim();
  if (!safeValue) {
    return getDisplayFallback(padLength);
  }

  if (padLength > 0 && /^-?\d+$/.test(safeValue)) {
    const sign = safeValue.startsWith('-') ? '-' : '';
    const digits = safeValue.replace(/^-/, '');
    return `${sign}${digits.padStart(padLength, '0')}`;
  }

  return safeValue;
};

const resolveStreamPart = ({
  part,
  configuredStream,
  devices,
  datastreams,
  did,
  tid,
  fallbackValue,
}) => {
  const safeConfiguredStream = configuredStream || {};
  const configuredDeviceId = Number(safeConfiguredStream?.deviceId);
  let targetDid = did;
  let targetTid = tid;
  let targetDatastreams = Array.isArray(datastreams) ? datastreams.filter(Boolean) : [];

  if (configuredDeviceId > 0) {
    const matchedDevice = (devices || []).find(
      (device) => Number(device?.id) === configuredDeviceId
    );

    if (matchedDevice) {
      targetDid = matchedDevice?.did ?? targetDid;
      targetTid = matchedDevice?.template?.tid ?? targetTid;
      targetDatastreams = (matchedDevice?.template?.datastreams || []).filter(Boolean);
    }
  }

  const matchedDatastream = targetDatastreams.find((datastream) => {
    if (
      safeConfiguredStream?.dataId !== null &&
      safeConfiguredStream?.dataId !== undefined &&
      Number(datastream?.id) === Number(safeConfiguredStream.dataId)
    ) {
      return true;
    }

    if (safeConfiguredStream?.pin) {
      return String(datastream?.pin || '') === String(safeConfiguredStream.pin);
    }

    return false;
  });

  const streamMin = toFiniteNumber(matchedDatastream?.min, part.fallbackMin);
  const rawStreamMax = toFiniteNumber(matchedDatastream?.max, part.fallbackMax);
  const streamMax = rawStreamMax >= streamMin ? rawStreamMax : streamMin;
  const hasDatastreamBinding =
    safeConfiguredStream?.dataId !== null &&
      safeConfiguredStream?.dataId !== undefined
      ? true
      : Boolean(safeConfiguredStream?.pin);

  return {
    enabled: isTimeRangePartEnabled(part.key, configuredStream),
    value:
      matchedDatastream?.value ??
      (hasDatastreamBinding
        ? safeConfiguredStream?.value ?? fallbackValue
        : fallbackValue),
    pin: matchedDatastream?.pin || safeConfiguredStream?.pin || '',
    did: targetDid,
    tid: targetTid,
    min: streamMin,
    max: streamMax,
  };
};

const buildValueState = (resolvedParts) =>
  TIME_RANGE_PARTS.reduce((acc, part) => {
    const partState = resolvedParts[part.key];
    acc[part.key] = normalizeIntegerString(partState?.value, partState?.min ?? part.fallbackMin);
    return acc;
  }, {});

const getSeparatorToken = (currentPart, nextPart) => {
  if (!currentPart || !nextPart) return null;

  if (currentPart.group === 'date' && nextPart.group === 'date') {
    return { type: 'separator', char: '-' };
  }

  if (currentPart.group === 'time' && nextPart.group === 'time') {
    return { type: 'separator', char: ':' };
  }

  return { type: 'gap' };
};

const getPartColumnWidth = (partKey, visibleCount) => {
  if (partKey === 'year') {
    return visibleCount >= 5 ? '1.3fr' : '1.16fr';
  }

  if (visibleCount >= 6) {
    return '0.9fr';
  }

  return '1fr';
};

const getPartValueScale = (partKey, visibleCount) => {
  if (partKey === 'year') {
    return visibleCount >= 5 ? 0.78 : 0.88;
  }

  return 1;
};

const TimeRange = ({
  title,
  transparentBackground,
  draggable,
  dims,
  streams,
  datastreams,
  devices,
  did,
  tid,
  showLeadingZeroHours = true,
  showLeadingZeroMinutes = true,
  cardColor,
  borderColor,
  titleColor,
  valueColor,
  shadowColor,
  borderRadius = 20,
  titleSize = 20,
  valueSize = 64,
}) => {
  const widgetTitle = String(title ?? '').trim();
  const canControlFromView = !draggable;
  const [now, setNow] = useState(() => new Date());
  const [editingPart, setEditingPart] = useState(null);
  const editingPartRef = useRef(null);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const fallbackClockValues = useMemo(() => getCurrentTimeRangePartValues(now), [now]);

  const resolvedParts = useMemo(
    () =>
      TIME_RANGE_PARTS.reduce((acc, part) => {
        acc[part.key] = resolveStreamPart({
          part,
          configuredStream: streams?.[part.key],
          devices,
          datastreams,
          did,
          tid,
          fallbackValue: fallbackClockValues[part.key],
        });
        return acc;
      }, {}),
    [datastreams, devices, did, fallbackClockValues, streams, tid]
  );

  const [draftValues, setDraftValues] = useState(() => buildValueState(resolvedParts));
  const lastCommittedRef = useRef(buildValueState(resolvedParts));

  useEffect(() => {
    editingPartRef.current = editingPart;
  }, [editingPart]);

  useEffect(() => {
    const nextValues = buildValueState(resolvedParts);
    const activeEditingPart = editingPartRef.current;

    setDraftValues((prev) => {
      let hasChanged = false;
      const next = { ...prev };

      TIME_RANGE_PARTS.forEach((part) => {
        if (activeEditingPart !== part.key && prev[part.key] !== nextValues[part.key]) {
          next[part.key] = nextValues[part.key];
          hasChanged = true;
        }
      });

      return hasChanged ? next : prev;
    });

    TIME_RANGE_PARTS.forEach((part) => {
      if (activeEditingPart !== part.key) {
        lastCommittedRef.current[part.key] = nextValues[part.key];
      }
    });
  }, [resolvedParts]);

  const visibleParts = useMemo(
    () => TIME_RANGE_PARTS.filter((part) => resolvedParts[part.key]?.enabled),
    [resolvedParts]
  );

  const layoutTokens = useMemo(() => {
    const tokens = [];

    visibleParts.forEach((part, index) => {
      tokens.push({ type: 'part', part });
      const separatorToken = getSeparatorToken(part, visibleParts[index + 1]);
      if (separatorToken) {
        tokens.push(separatorToken);
      }
    });

    return tokens;
  }, [visibleParts]);

  const gridTemplateColumns = useMemo(
    () =>
      layoutTokens
        .map((token) => {
          if (token.type === 'part') {
            return `minmax(0, ${getPartColumnWidth(token.part.key, visibleParts.length)})`;
          }

          if (token.type === 'gap') return 'clamp(8px, 1.2vw, 18px)';
          return 'clamp(8px, 1.4vw, 16px)';
        })
        .join(' '),
    [layoutTokens, visibleParts.length]
  );

  const widgetDims = useMemo(() => {
    const width = Number(dims?.width) || 620;
    const height = Number(dims?.height) || 260;
    const visibleCount = Math.max(visibleParts.length, 1);
    const widthTarget =
      visibleCount <= 3 ? 720 : visibleCount === 4 ? 860 : visibleCount === 5 ? 1080 : 1320;
    const heightTarget = visibleCount <= 4 ? 320 : 340;
    const scale = clamp(Math.min(width / widthTarget, height / heightTarget), 0.4, 2.15);
    const densityScale =
      visibleCount <= 3 ? 1.04 : visibleCount === 4 ? 0.9 : visibleCount === 5 ? 0.72 : 0.56;

    return {
      padding: `${Math.round(clamp(28 * scale, 12, 42))}px`,
      radius: `${Math.round(clamp(Number(borderRadius || 20) * 1.8, 18, 56))}px`,
      headerGap: `${Math.round(clamp(18 * scale, 8, 28))}px`,
      titleSize: `${Math.round(clamp(Number(titleSize || 20) * scale * 1.45, 16, 52))}px`,
      framePadding: `${Math.round(clamp(18 * scale, 8, 26))}px`,
      frameRadius: `${Math.round(clamp(Number(borderRadius || 20) * 0.8, 14, 30))}px`,
      tileRadius: `${Math.round(clamp(18 * scale, 10, 26))}px`,
      tileHeight: `${Math.round(clamp(128 * scale, 56, 170))}px`,
      tileValueSize: `${Math.round(
        clamp(Number(valueSize || 64) * scale * densityScale, 18, 104)
      )}px`,
      separatorSize: `${Math.round(clamp(Number(valueSize || 64) * scale * 0.22, 10, 28))}px`,
      labelSize: `${Math.round(clamp(24 * scale, 10, 34))}px`,
      labelGap: `${Math.round(clamp(18 * scale, 6, 22))}px`,
      toggleWidth: `${Math.round(clamp(74 * scale, 46, 96))}px`,
      toggleHeight: `${Math.round(clamp(40 * scale, 28, 52))}px`,
      toggleInset: `${Math.round(clamp(5 * scale, 3, 6))}px`,
      cornerSize: `${Math.round(clamp(28 * scale, 18, 34))}px`,
    };
  }, [borderRadius, dims?.height, dims?.width, titleSize, valueSize, visibleParts.length]);

  const pushToDevice = async (partKey, nextValue) => {
    const target = resolvedParts[partKey];
    if (!target?.did || !target?.pin) return;

    const body = JSON.stringify({
      device: target.did,
      template: target.tid,
      data: { [target.pin]: nextValue },
    });

    try {
      await axios.post(`${SENSORS_URL}/control`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (err) {
      console.log(err?.message || String(err));
    }
  };

  const commitPart = (partKey, rawValue) => {
    const target = resolvedParts[partKey];
    const parsedValue = Number(rawValue);
    if (!Number.isFinite(parsedValue) || !target) return;

    const nextValue = String(Math.round(clamp(parsedValue, target.min, target.max)));

    setDraftValues((prev) => ({
      ...prev,
      [partKey]: nextValue,
    }));
    lastCommittedRef.current[partKey] = nextValue;
    pushToDevice(partKey, nextValue);
  };

  const canEditPart = (partKey) => {
    const target = resolvedParts[partKey];
    return canControlFromView && Boolean(target?.did && target?.pin);
  };

  const startEdit = (partKey) => {
    if (!canEditPart(partKey)) return;

    setDraftValues((prev) => ({
      ...prev,
      [partKey]: lastCommittedRef.current[partKey],
    }));
    setEditingPart(partKey);
  };

  const handleDraftChange = (partKey, rawValue) => {
    if (!/^-?\d*$/.test(String(rawValue))) return;

    setDraftValues((prev) => ({
      ...prev,
      [partKey]: String(rawValue),
    }));
  };

  const cancelEdit = (partKey) => {
    setDraftValues((prev) => ({
      ...prev,
      [partKey]: lastCommittedRef.current[partKey],
    }));
    setEditingPart(null);
  };

  const finalizeEdit = (partKey) => {
    const rawValue = String(draftValues[partKey] ?? '');

    if (rawValue === '' || rawValue === '-' || !Number.isFinite(Number(rawValue))) {
      cancelEdit(partKey);
      return;
    }

    commitPart(partKey, rawValue);
    setEditingPart(null);
  };

  const setCursor = (target, cursor) => {
    if (!draggable || !target) return;
    target.style.cursor = cursor;
  };

  const widgetAriaLabel = useMemo(
    () =>
      layoutTokens
        .map((token) => {
          if (token.type === 'part') {
            const padLength = getPartPadLength(
              token.part.key,
              showLeadingZeroHours,
              showLeadingZeroMinutes
            );
            return formatPartValue(draftValues[token.part.key], padLength);
          }

          if (token.type === 'separator') {
            return token.char;
          }

          return ' ';
        })
        .join(''),
    [draftValues, layoutTokens, showLeadingZeroHours, showLeadingZeroMinutes]
  );

  const renderPartTile = (part) => {
    const padLength = getPartPadLength(part.key, showLeadingZeroHours, showLeadingZeroMinutes);
    const isEditable = canEditPart(part.key);
    const inputWidth = Math.max(part.padLength, 2);
    const commonProps = {
      className: `time-range-widget__tile ${isEditable ? 'time-range-widget__tile--interactive' : ''}`,
      style: {
        '--trw-input-ch': inputWidth,
        '--trw-part-scale': getPartValueScale(part.key, visibleParts.length),
      },
    };

    if (editingPart === part.key) {
      return (
        <div {...commonProps}>
          <input
            type="text"
            inputMode="numeric"
            autoFocus
            value={draftValues[part.key]}
            className="time-range-widget__tileInput"
            aria-label={`${part.label} value`}
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.target.select()}
            onChange={(e) => handleDraftChange(part.key, e.target.value)}
            onBlur={() => finalizeEdit(part.key)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
                finalizeEdit(part.key);
              }

              if (e.key === 'Escape') {
                e.preventDefault();
              cancelEdit(part.key);
            }
          }}
        />
        </div>
      );
    }

    const content = (
      <>
        <span className="time-range-widget__tileValue">
          {formatPartValue(draftValues[part.key], padLength)}
        </span>
      </>
    );

    if (!isEditable) {
      return <div {...commonProps}>{content}</div>;
    }

    return (
      <button
        type="button"
        {...commonProps}
        aria-label={`Edit ${part.label}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => startEdit(part.key)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            startEdit(part.key);
          }
        }}
      >
        {content}
      </button>
    );
  };

  return (
    <div
      className={`widget-container time-range-widget ${transparentBackground ? 'is-transparent' : ''}`}
      draggable={draggable}
      onMouseDown={(e) => setCursor(e.currentTarget, 'grabbing')}
      onMouseUp={(e) => setCursor(e.currentTarget, 'grab')}
      onMouseLeave={(e) => setCursor(e.currentTarget, draggable ? 'grab' : 'default')}
      onDragStart={(e) => {
        const id = e.target.closest('.widget').id;
        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;

        let dragData = `time-range;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
      style={{ cursor: draggable ? 'grab' : 'default' }}
    >
      <div
        className="time-range-widget__card"
        style={{
          '--trw-card-color': cardColor || 'var(--time-range-card-bg)',
          '--trw-border-color': borderColor || 'var(--time-range-border-color)',
          '--trw-title-color': titleColor || 'var(--time-range-title-color)',
          '--trw-value-color': valueColor || 'var(--time-range-value-color)',
          '--trw-shadow-color': shadowColor || 'var(--time-range-shadow-color)',
          '--trw-padding': widgetDims.padding,
          '--trw-radius': widgetDims.radius,
          '--trw-header-gap': widgetDims.headerGap,
          '--trw-title-size': widgetDims.titleSize,
          '--trw-frame-padding': widgetDims.framePadding,
          '--trw-frame-radius': widgetDims.frameRadius,
          '--trw-tile-radius': widgetDims.tileRadius,
          '--trw-tile-height': widgetDims.tileHeight,
          '--trw-tile-value-size': widgetDims.tileValueSize,
          '--trw-separator-size': widgetDims.separatorSize,
          '--trw-label-size': widgetDims.labelSize,
          '--trw-label-gap': widgetDims.labelGap,
          '--trw-toggle-width': widgetDims.toggleWidth,
          '--trw-toggle-height': widgetDims.toggleHeight,
          '--trw-toggle-inset': widgetDims.toggleInset,
          '--trw-corner-size': widgetDims.cornerSize,
        }}
      >
        <div className="time-range-widget__topbar">
          {widgetTitle ? <h3 className="time-range-widget__title">{widgetTitle}</h3> : <span />}
          <div
            className={`time-range-widget__statusToggle ${visibleParts.length > 0 ? 'is-on' : ''}`}
            aria-hidden="true"
          >
            <span className="time-range-widget__statusThumb" />
          </div>
        </div>

        <div className="time-range-widget__body" aria-label={widgetAriaLabel}>
          {visibleParts.length > 0 ? (
            <>
              <div
                className="time-range-widget__frame"
                style={{ gridTemplateColumns }}
              >
                {layoutTokens.map((token, index) => {
                  if (token.type === 'part') {
                    return (
                      <div className="time-range-widget__frameCell" key={`${token.part.key}-${index}`}>
                        {renderPartTile(token.part)}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={`${token.type}-${index}`}
                      className={`time-range-widget__separator ${
                        token.type === 'gap' ? 'time-range-widget__separator--gap' : ''
                      }`}
                      aria-hidden="true"
                    >
                      {token.type === 'separator' ? token.char : ''}
                    </div>
                  );
                })}
              </div>

              <div
                className="time-range-widget__labels"
                style={{ gridTemplateColumns }}
              >
                {layoutTokens.map((token, index) => {
                  if (token.type === 'part') {
                    return (
                      <div className="time-range-widget__label" key={`label-${token.part.key}-${index}`}>
                        {token.part.label}
                      </div>
                    );
                  }

                  return <div key={`label-gap-${index}`} aria-hidden="true" />;
                })}
              </div>
            </>
          ) : (
            <div className="time-range-widget__empty">
              Enable at least one date or time part.
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default TimeRange;
